/* ygh-track.js: one event layer for the whole store.
 *
 *   window.yghTrack(name, key, val, extra)   or   document.dispatchEvent(new CustomEvent('ygh:track', {detail:{event, key, val}}))
 *
 * Every event goes to four places at once:
 *   GA4      gtag('event', name, {send_to: G-…})   the gtag loaded by the theme (same G- id TagFly uses), pinned to GA4 so the Google Ads
 *            tag on the same gtag never receives them. GA4 drops everything past
 *            500 events per session, so the noisy names (GA_NOISY) stop going to GA4 after 300 events in a session; callback, survey,
 *            upsell, gallery milestones, cart and form events always go.
 *   GTM      dataLayer.push({event: name, ygh: {...}})   for custom-event triggers in the TagFly container
 *   Clarity  clarity('event', name)               recordings filterable by Custom events
 *   IG       igEvents.push({event: name})          Intelligems metrics (whitelist: no section/scroll/click noise)
 *   Worker   sendBeacon to YGH_EVENTS_URL           our own D1 store: sessions, funnels, US-only reports
 *
 * Automatic events (no per-section code):
 *   section_reach  key = section id suffix, val = position   once per section, the moment any of it enters the viewport ("scrolled to")
 *   section_view   key = section id suffix, val = position   once per section, after 1 s of being "in view": at least half of the
 *                  section visible, or the section covering at least 60% of the viewport (tall sections, and jumps from buttons that
 *                  scroll into the middle of a section). Checked on every 2% visibility change and on every scroll stop.
 *   ui_click       key = "<section>|<label>"                 every a / button / label / summary tap
 *   scroll_depth   val = 25 / 50 / 75 / 100
 *   time_on_page   val = 15 / 45 / 90 / 180 seconds, visible time only
 *   video_play     key = "tour|<file>" (pop-up tour / founders video), "gallery|<file>" (gallery video slide), "<section>|<file>";
 *                  extra.auto = 1 when nobody had interacted with the page yet (muted autoplay), native <video> and <wistia-player>
 *                  YouTube embeds too: key "<section>|yt:<id>" (owner interviews, review stories). Any youtube iframe added to the page
 *                  gets enablejsapi=1 and is asked to stream its state (postMessage "listening"), no YouTube API script needed.
 *   video_progress key = same, val = 25 / 50 / 75 / 95;  video_complete on ended (Wistia: percent-watched-change / ended)
 *   wistia_visitor key = Wistia visitor key of this browser (first Wistia play), links a session to its Wistia heatmap
 *   After an accepted HubSpot submission (ygh:lead carries the email) the address is handed to every Wistia player on this and
 *   later pages (localStorage ygh_wistia_email), so the viewer shows up by name in Wistia's heatmaps. Never sent to the worker.
 *   tooltip_open   key = data-ig-tip / data-ygh-tip           hover (desktop) or tap (touch), 5 s per tooltip
 *   upsell_add / upsell_remove   key = data-ig-add, extra.after_tip = the shopper opened that tooltip first
 *   gallery_slide  key = slide number, val = total, extra.kind = image | video   (active thumb changes)
 *   gallery_video_view / gallery_mid / gallery_complete   the 2nd slide (video), 6th slide, last slide
 *   gallery_dwell  key = slide number, val = seconds spent on it (sent when the photo changes or the page is left)
 *   gallery_zoom   key = slide opened in the full-screen photo viewer, val = total; gallery_zoom_slide = every further photo looked at inside it
 *   Gallery video slide (Nord heroes, sent by new-pdp-nord-hero.js): gallery_video_start (extra.sound = 1 started with sound, 0 muted),
 *   gallery_video_expand (val = second it was at), gallery_video_sound (Tap for sound), gallery_video_mute (val 1 = muted, 0 = unmuted),
 *   gallery_video_pause (val = second), tour_open key = expand | row | circle | section (what opened the tour pop-up)
 *   swipe_row      key = section, val = 50 | 100   any other sideways-scrolling block (comparison table) swiped half way / to the end
 *   ui_click labels: when a section has several buttons with the same words, the label names the card and the position: "CHOOSE · Nordwood Classic (2 of 3)"
 *   carousel_step  key = section, val = card reached (each further card once), extra.total   swipe rows: delivery steps, owner story points
 *   cart_open, cart_remove, cart_qty, checkout_click (val = total shown)
 *   Purchase attribution: when the cart is opened, Order now is pressed or Checkout is pressed, the cart gets the hidden attributes
 *   _ygh_sid / _ygh_vid (underscore = never shown to the shopper). They arrive on the Shopify order; the events worker polls orders
 *   and marks the session as purchased. Nothing else about the cart is touched.
 *   callback_open / callback_submit   every "request a callback" trigger and accepted HubSpot submission (key = form id)
 *   survey_open / survey_submit       the survey sections (new-survey-v3, "Take the survey" button) and their submissions
 *   form_submit    every accepted HubSpot submission, key = form id, extra.kind = callback | survey | contact | form
 *   video_unmute   key = same as video_play: sound turned on in a video that started muted. Muted starts are never counted as deliberate plays.
 *   drawer_open    key = drawer id (spec / shipping / faq drawers that toggle .is-open)
 *
 * Config comes from snippets/ygh-track.liquid (window.__yghTrackCfg): endpoint, page template, product,
 * price shown. Everything is best-effort and wrapped so a failure never touches the page.
 */
(function () {
  'use strict';
  if (window.yghTrack) return;
  var CFG = window.__yghTrackCfg || {};
  if (CFG.enabled === false) { window.yghTrack = function () {}; return; }
  var W = window, D = document;
  var GA_NOISY = { ui_click: 1, section_view: 1, section_reach: 1, scroll_depth: 1, time_on_page: 1, video_progress: 1, gallery_slide: 1, gallery_dwell: 1, gallery_zoom_slide: 1 };
  var IG_ALLOW = { tooltip_open: 1, upsell_add: 1, upsell_remove: 1, gallery_video_view: 1, gallery_mid: 1, gallery_complete: 1, video_play: 1,
    cart_open: 1, checkout_click: 1, form_submit: 1, callback_open: 1, callback_submit: 1, survey_open: 1, survey_submit: 1, drawer_open: 1, order_now_click: 1, quiz_start: 1, quiz_result: 1,
    product_card_click: 1, hero_cta_click: 1 };

  // ---- ids: visitor (1 y), session (30 min idle), pageview ----
  function rnd() { var s = ''; while (s.length < 16) s += Math.floor(Math.random() * 16).toString(16); return s; }
  function store(kind, key, val) {
    try { var s = kind === 'l' ? localStorage : sessionStorage; if (val === undefined) return s.getItem(key); s.setItem(key, val); } catch (e) { return null; }
  }
  var vid = store('l', 'ygh_vid'); if (!vid) { vid = rnd(); store('l', 'ygh_vid', vid); }
  var now = Date.now();
  var sid = store('l', 'ygh_sid'), sidAt = parseInt(store('l', 'ygh_sid_at') || '0', 10);
  if (!sid || now - sidAt > 30 * 60 * 1000) { sid = rnd(); store('l', 'ygh_sid', sid); }
  store('l', 'ygh_sid_at', String(now));
  var pv = rnd();
  var t0 = now;
  var mobile = W.innerWidth < 750;
  // traffic source: utm_* on this landing, remembered for the session (utm_source falls back to gclid / fbclid / ttclid)
  var q = {}; try { location.search.replace(/^\?/, '').split('&').forEach(function (kv) { var i = kv.indexOf('='); if (i > 0) q[decodeURIComponent(kv.slice(0, i))] = decodeURIComponent(kv.slice(i + 1).replace(/\+/g, ' ')); }); } catch (e) {}
  var utm = null;
  if (q.utm_source || q.gclid || q.fbclid || q.ttclid) utm = { s: (q.utm_source || (q.gclid ? 'google' : q.fbclid ? 'facebook' : 'tiktok')).slice(0, 40), m: (q.utm_medium || (q.gclid || q.fbclid || q.ttclid ? 'cpc' : '')).slice(0, 40), c: (q.utm_campaign || '').slice(0, 80), x: (q.utm_content || q.utm_term || '').slice(0, 80) };
  if (utm) store('l', 'ygh_utm', JSON.stringify(utm)); else { try { utm = JSON.parse(store('l', 'ygh_utm') || 'null'); } catch (e) { utm = null; } }
  if (utm && sidAt && now - sidAt > 30 * 60 * 1000 && !q.utm_source) utm = null;   // stale source from an older session
  var pageN = (parseInt(store('s', 'ygh_pn') || '0', 10) || 0) + 1; store('s', 'ygh_pn', String(pageN));
  var page = {
    path: location.pathname, tpl: CFG.tpl || '', prod: CFG.prod || '', price: CFG.price || 0,
    dev: mobile ? 'm' : 'd', ref: (function () { try { return D.referrer ? new URL(D.referrer).hostname : ''; } catch (e) { return ''; } })(),
    w: W.innerWidth, h: W.innerHeight, utm: utm, pn: pageN,
    // Meta's browser / click cookies, so the events worker can send server-side signals (HighIntentVisit) Meta can match
    fb: (function () { try { var m = {}; (D.cookie || '').split(';').forEach(function (c) { var kv = c.trim().split('='); if (kv[0] === '_fbp') m.p = (kv[1] || '').slice(0, 80); if (kv[0] === '_fbc') m.c = (kv[1] || '').slice(0, 260); }); return m; } catch (e) { return {}; } })()
  };
  // first touch: the source that brought this browser here the first time, kept 30 days and sent with every pageview (ft),
  // so a visitor who comes back from an open tab or a bookmark is not reported as "inside the site" / direct
  var ft = null; try { ft = JSON.parse(store('l', 'ygh_ft') || 'null'); } catch (e) { ft = null; }
  if (!ft || !ft.t || now - ft.t > 30 * 24 * 3600 * 1000) {
    ft = utm ? { s: utm.s, m: utm.m || '', c: utm.c || '', t: now } : { s: (page.ref && page.ref.indexOf('yourgreenhouses') < 0) ? page.ref : 'direct', m: '', c: '', t: now };
    store('l', 'ygh_ft', JSON.stringify(ft));
  }
  page.ft = { s: ft.s, m: ft.m, c: ft.c };

  // ---- fan-out ----
  var buffer = [];       // [dt, name, key, val, extra]
  var sent = 0;          // events already delivered to the worker
  var seenTip = {};      // tooltip keys opened in this pageview
  var intent = '', lastForm = '';   // last callback / survey button pressed; last form id reported (de-duplication)
  function toParams(name, key, val, extra) {
    var p = { ygh_key: key == null ? '' : String(key).slice(0, 100), ygh_val: typeof val === 'number' ? val : 0, ygh_tpl: page.tpl, ygh_prod: page.prod, ygh_dev: page.dev };
    if (extra) for (var k in extra) if (extra.hasOwnProperty(k) && p['ygh_' + k] === undefined) { var v = extra[k]; p['ygh_' + k] = typeof v === 'number' ? v : String(v).slice(0, 100); }
    return p;
  }
  function track(name, key, val, extra) {
    try {
      if (!name) return;
      name = String(name).replace(/[^a-z0-9_]/gi, '_').slice(0, 40);
      var rec = [Date.now() - t0, name, key == null ? '' : String(key).slice(0, 120), typeof val === 'number' ? val : 0, extra || null];
      buffer.push(rec);
      if (buffer.length - sent >= 40) send(false);
      var p = toParams(name, key, val, extra);
      try {
        var gaN = parseInt(store('s', 'ygh_ga_n') || '0', 10) || 0;
        // send_to pins the event to the GA4 property only: the same gtag also carries the Google Ads tag (AW-), which must not see these
        if (typeof W.gtag === 'function' && !(GA_NOISY[name] && gaN >= 300) && gaN < 480) { p.send_to = CFG.ga4 || 'G-C334J0DC0V'; W.gtag('event', name, p); store('s', 'ygh_ga_n', String(gaN + 1)); }
      } catch (e) {}
      try { W.dataLayer = W.dataLayer || []; W.dataLayer.push({ event: name, ygh: p }); } catch (e) {}
      try { if (typeof W.clarity === 'function') W.clarity('event', name); } catch (e) {}
      try { if (IG_ALLOW[name]) { W.igEvents = W.igEvents || []; W.igEvents.push({ event: name }); } } catch (e) {}
      if (CFG.debug) console.info('[ygh track]', name, key, val, extra || '');
    } catch (e) {}
  }
  W.yghTrack = track;
  D.addEventListener('ygh:track', function (e) { var d = e.detail || {}; track(d.event || d.name, d.key, d.val, d.extra); });

  // ---- worker delivery: one row per pageview, replaced on every send (2-3 writes per pageview) ----
  var sendTimer = null;
  function send(final) {
    if (!CFG.endpoint || !buffer.length) return;
    var body = JSON.stringify({ v: 1, pv: pv, vid: vid, sid: sid, t0: t0, dur: Date.now() - t0, page: page, ev: buffer, done: !!final });
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(CFG.endpoint, body)) { sent = buffer.length; return; }
    } catch (e) {}
    try { fetch(CFG.endpoint, { method: 'POST', body: body, keepalive: true, mode: 'no-cors' }); sent = buffer.length; } catch (e) {}
  }
  function schedule(ms) { clearTimeout(sendTimer); sendTimer = setTimeout(function () { send(false); }, ms); }
  D.addEventListener('visibilitychange', function () { if (D.visibilityState === 'hidden') send(true); });
  W.addEventListener('pagehide', function () { send(true); });
  schedule(8000);                 // an early copy in case the tab dies before pagehide
  setInterval(function () { if (buffer.length > sent) send(false); }, 60000);

  // ---- purchase attribution: session id on the cart ----
  function tagCart(keepalive) {
    try {
      if (store('s', 'ygh_cart_tag') === sid) return;
      fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ attributes: { _ygh_sid: sid, _ygh_vid: vid } }), keepalive: !!keepalive, credentials: 'same-origin' })
        .then(function (r) { if (r.ok) store('s', 'ygh_cart_tag', sid); }).catch(function () {});
    } catch (e) {}
  }
  D.addEventListener('pointerdown', function (e) { var el = e.target && e.target.closest ? e.target.closest('[data-ygh-checkout], [name="checkout"], a[href*="/checkout"]') : null; if (el) tagCart(true); }, true);

  // ---- helpers ----
  function sectionOf(el) {
    // the side cart and the mobile menu live inside the header section: report them as their own places
    var ov = el && el.closest ? el.closest('#mini-cart, .cart-drawer-container, #menu-drawer, .menu-drawer-container, header-drawer') : null;
    if (ov) return (ov.id === 'menu-drawer' || ov.tagName === 'HEADER-DRAWER' || ov.classList.contains('menu-drawer-container')) ? 'menu' : 'cart';
    var s = el && el.closest ? el.closest('.shopify-section, [data-section-id]') : null;
    if (!s) return '';
    var id = s.id || s.getAttribute('data-section-id') || '';
    var i = id.lastIndexOf('__');
    var key = (i > -1 ? id.slice(i + 2) : id.replace(/^shopify-section-/, '')).slice(0, 60);
    return key === 'MainContent' ? '' : key;
  }
  function labelOf(el) {
    var t = el.getAttribute('aria-label') || el.getAttribute('data-track-label') || (el.innerText || el.textContent || '');
    t = String(t).replace(/\s+/g, ' ').trim();
    if (!t) { var img = el.querySelector && el.querySelector('img[alt]'); if (img) t = img.alt; }
    if (!t && el.href) t = el.href.replace(/[?#].*/, '').split('/').pop();
    return t.slice(0, 40);
  }
  function fileOf(src) { return String(src || '').replace(/[?#].*/, '').split('/').pop().slice(0, 40); }

  // ---- section views ----
  function sectionViews() {
    // every section wrapper, sized when it is checked and not here: while the page is still rendering the theme collapses off-screen
    // sections to zero height (content-visibility: auto), so a height filter at boot dropped everything below the first section
    var secs = Array.prototype.slice.call(D.querySelectorAll('.shopify-section'));
    var timers = {}, done = {}, reached = {};   // reached = its top entered the viewport at all (no dwell): "scrolled to", next to "looked at"
    function inView(el) {
      var r = el.getBoundingClientRect(), vh = W.innerHeight || 1, vw = W.innerWidth || 1;
      var visH = Math.min(r.bottom, vh) - Math.max(r.top, 0), visW = Math.min(r.right, vw) - Math.max(r.left, 0);
      if (visH <= 0 || visW <= 0 || r.height <= 40) return false;
      return visH / r.height >= 0.5 || visH >= vh * 0.6;
    }
    function check() {
      secs.forEach(function (el) {
        var key = sectionOf(el); if (!key) return;
        if (!reached[key]) { var rr = el.getBoundingClientRect(); if (rr.height > 40 && rr.top < (W.innerHeight || 1) && rr.bottom > 0) { reached[key] = 1; track('section_reach', key, secs.indexOf(el) + 1); } }
        if (done[key]) return;
        var vis = inView(el);
        if (vis && !timers[key]) {
          timers[key] = setTimeout(function () {
            if (!inView(el)) { timers[key] = null; return; }
            done[key] = 1;
            var h = el.querySelector('h1, h2, .h1, .h2');
            track('section_view', key, secs.indexOf(el) + 1, { label: h ? (h.innerText || '').replace(/\s+/g, ' ').trim().slice(0, 60) : '' });
          }, 1000);
        } else if (!vis && timers[key]) { clearTimeout(timers[key]); timers[key] = null; }
      });
    }
    var t = null; function onScroll() { clearTimeout(t); t = setTimeout(check, 120); }
    W.addEventListener('scroll', onScroll, { passive: true }); W.addEventListener('resize', onScroll, { passive: true });
    if ('IntersectionObserver' in W) {   // fine-grained visibility changes (2% steps) so tall sections and jumps are caught without waiting for a scroll event
      var steps = []; for (var i = 0; i <= 50; i++) steps.push(i / 50);
      var io = new IntersectionObserver(function () { check(); }, { threshold: steps });
      secs.forEach(function (s) { io.observe(s); });
    }
    check(); setTimeout(check, 1500);
  }

  // ---- clicks ----
  D.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('a, button, [role="button"], label, summary, input[type="submit"]') : null;
    if (!el) return;
    if (el.hasAttribute('data-npwmp-thumb')) return; // gallery_slide covers thumbs
    var label = labelOf(el); if (!label) return;
    var sec = sectionOf(el);
    var extra = {};
    // several buttons with the same words in one section ("CHOOSE" x3): say which one. "CHOOSE · Nordwood Classic (2 of 3)"
    try {
      var host = el.closest('.shopify-section, [data-section-id]');
      if (host && label.length < 30) {
        var same = Array.prototype.filter.call(host.querySelectorAll('a, button, [role="button"]'), function (x) { return x.offsetParent !== null && labelOf(x) === label; });
        if (same.length > 1 && same.length <= 12) {
          var ctx = '', card = el.parentElement;
          for (var up = 0; up < 5 && card && card !== host && !ctx; up++, card = card.parentElement) {
            var hd = card.querySelector('h2, h3, h4, [class*="title"], [class*="name"], [class*="heading"]');
            if (hd && !hd.contains(el)) { var tx = (hd.innerText || hd.textContent || '').replace(/\s+/g, ' ').trim(); if (tx && tx !== label && tx.length < 60) ctx = tx; }
          }
          if (!ctx && el.href) ctx = String(el.href).replace(/[?#].*/, '').replace(/\/$/, '').split('/').pop().replace(/-/g, ' ');
          var pos = same.indexOf(el) + 1;
          extra.pos = pos; extra.of = same.length;
          label = (label + (ctx ? ' · ' + ctx.slice(0, 34) : '') + ' (' + pos + ' of ' + same.length + ')').slice(0, 90);
        }
      }
    } catch (err) {}
    if (el.href) extra.href = String(el.href).replace(/^https?:\/\/[^/]+/, '').slice(0, 100);
    track('ui_click', sec + '|' + label, 0, extra);
    if (el.hasAttribute('data-ygh-checkout')) {
      var tot = D.querySelector('#mini-cart [data-ygh-total]');
      track('checkout_click', page.prod, tot ? parseInt(String(tot.textContent).replace(/[^0-9]/g, ''), 10) || 0 : 0);
      tagCart(true);
    }
    if (el.hasAttribute('data-ygh-remove')) track('cart_remove', sec);
    if (el.hasAttribute('data-ygh-qty')) track('cart_qty', el.getAttribute('data-ygh-qty'));
    if (el.hasAttribute('data-callback-open') || el.hasAttribute('data-callback-card') || el.hasAttribute('data-cbm-open') || /callback|call back|call me|request a call/i.test(label)) track('callback_open', sec + '|' + label);
    if (el.hasAttribute('data-callback-open') || el.hasAttribute('data-callback-card') || el.hasAttribute('data-cbm-open') || /callback|call back|call me|request a call/i.test(label)) intent = 'callback';
    if (/^nn-surv2-embed/.test(el.getAttribute('aria-controls') || '') || (/survey/i.test(sec) && el.tagName === 'BUTTON') || /take the .*survey|start survey/i.test(label)) { intent = 'survey'; track('survey_open', sec + '|' + label); }
    // the call version of a landing page makes the buy button open the callback form (data-callback-open): that is a callback_open above, not an order
    if (el.matches('[data-npwmp-cta], [data-npwmp-buy-alone]') && !el.hasAttribute('data-callback-open')) {
      var chk = D.querySelectorAll('input[data-npwmp-accessory]:checked, input[data-ig-add]:checked').length;
      track('order_now_click', page.prod, page.price, { addons: chk, label: label });
      setTimeout(function () { tagCart(false); }, 2500);
    }
  }, true);

  // ---- scroll depth + time on page ----
  (function () {
    var marks = [25, 50, 75, 100], hit = {};
    function onScroll() {
      var h = Math.max(D.documentElement.scrollHeight, D.body.scrollHeight) - W.innerHeight; if (h <= 0) return;
      var pct = Math.round((W.pageYOffset / h) * 100);
      marks.forEach(function (m) { if (pct >= m && !hit[m]) { hit[m] = 1; track('scroll_depth', page.tpl, m); } });
    }
    W.addEventListener('scroll', onScroll, { passive: true }); setTimeout(onScroll, 1500);
    var vis = 0, last = Date.now(), tm = [15, 45, 90, 180], ti = 0;
    setInterval(function () {
      var n = Date.now(); if (D.visibilityState !== 'hidden') vis += n - last; last = n;
      if (ti < tm.length && vis >= tm[ti] * 1000) { track('time_on_page', page.tpl, tm[ti]); ti++; }
    }, 1000);
  })();

  // ---- videos: native + wistia ----
  (function () {
    var seen = {}, prog = {}, wk = false, wasMuted = {}, unmuted = {};
    // sound turned on in a video that started muted (sale / video page hero): the deliberate "I am watching this" moment
    ['volumechange', 'mute-change', 'volume-change'].forEach(function (ev) { D.addEventListener(ev, function (e) {
      var el = e.target; if (!isPlayer(el) || el.muted) return;
      var k = keyOf(el); if (!wasMuted[k] || unmuted[k]) return; unmuted[k] = 1;
      track('video_unmute', k, Math.round(el.currentTime || 0));
    }, true); });
    function isPlayer(el) { return el && (el.tagName === 'VIDEO' || el.tagName === 'WISTIA-PLAYER'); }
    function wistiaKey(el) {
      if (wk !== false) return; wk = null;
      try { var k = typeof el.visitorKey === 'function' ? el.visitorKey() : el.visitorKey; if (k) { wk = String(k); track('wistia_visitor', wk); } } catch (e) {}
    }
    // key = where|file. where: "tour" = the pop-up player (Watch product tour / founders video), "gallery" = the inline gallery
    // video slide (autoplays muted), else the section id. extra.auto = 1 when the play happened without any user gesture.
    function keyOf(el) {
      var f = el.tagName === 'WISTIA-PLAYER' ? (el.getAttribute('media-id') || 'wistia') : fileOf(el.currentSrc || el.src || (el.querySelector('source') || {}).src);
      var where = el.closest('[data-npwmp-vmodal]') ? 'tour' : el.closest('[data-npwmp-stage], [data-npwmp-lightbox], .gvsl') ? 'gallery' : sectionOf(el);
      return where + '|' + f;
    }
    D.addEventListener('play', function (e) {
      var el = e.target; if (!isPlayer(el)) return;
      var k = keyOf(el); if (seen[k]) return; seen[k] = 1;
      // the gallery slide autoplays whenever the shopper reaches it, so it is never a deliberate play (gallery_video_view covers it)
      var auto = k.indexOf('gallery|') === 0 ? 1 : navigator.userActivation ? (navigator.userActivation.hasBeenActive ? 0 : 1) : (el.autoplay ? 1 : 0);
      // background clips (muted loops and muted autoplay that start when scrolled into view) are never a deliberate play, whatever happened before
      if (el.tagName === 'VIDEO' && el.muted && (el.loop || el.autoplay || !el.controls)) auto = 1;
      if (el.muted) wasMuted[k] = 1;
      track('video_play', k, 0, { muted: el.muted ? 1 : 0, auto: auto });
      if (el.tagName === 'WISTIA-PLAYER') setTimeout(function () { wistiaKey(el); }, 1500);
    }, true);
    function marks(k, pct) {
      var best = prog[k] || 0;
      [25, 50, 75, 95].forEach(function (m) { if (pct >= m && best < m) { best = m; track('video_progress', k, m); } });
      prog[k] = best;
    }
    function progress(e) {
      var el = e.target; if (!isPlayer(el)) return;
      var d = el.duration || 0, c = el.currentTime || 0; if (!(d > 0) || !(c > 0)) return;
      marks(keyOf(el), (c / d) * 100);
    }
    ['timeupdate', 'time-update', 'second-change'].forEach(function (ev) { D.addEventListener(ev, progress, true); });
    // videos already running when this script starts (autoplay hero loops): log them as automatic plays so progress has a play to belong to
    function catchUp() { Array.prototype.forEach.call(D.querySelectorAll('video'), function (v) { if (!v.paused && !v.ended) { var k = keyOf(v); if (!seen[k]) { seen[k] = 1; track('video_play', k, 0, { muted: v.muted ? 1 : 0, auto: 1 }); } } }); }
    if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', catchUp); else catchUp();
    setTimeout(catchUp, 3000);
    D.addEventListener('percent-watched-change', function (e) {   // Wistia: share of the video actually seen
      var el = e.target; if (!isPlayer(el)) return;
      var p = e.detail && (e.detail.percentWatched != null ? e.detail.percentWatched : e.detail.percent); if (p == null) p = el.percentWatched;
      if (typeof p === 'number') marks(keyOf(el), p <= 1 ? p * 100 : p);
    }, true);
    ['ended', 'end'].forEach(function (ev) { D.addEventListener(ev, function (e) {
      var el = e.target; if (!isPlayer(el)) return;
      var k = keyOf(el); if (prog[k] === 100) return; marks(k, 95); prog[k] = 100; track('video_complete', k, 100);
    }, true); });
  })();

  // ---- YouTube iframes: state + progress via the embed's postMessage channel ----
  (function () {
    var players = [], n = 0;
    function attach(ifr) {
      if (ifr.__ygh || !/youtube(-nocookie)?\.com\/embed\//.test(ifr.src || '')) return;
      ifr.__ygh = 1;
      var id = (ifr.src.match(/\/embed\/([^?&/]+)/) || [])[1] || 'yt';
      var rec = { el: ifr, key: sectionOf(ifr) + '|yt:' + id, title: (ifr.title || '').slice(0, 60), played: 0, best: 0, done: 0, n: ++n };
      players.push(rec);
      if (ifr.src.indexOf('enablejsapi=1') < 0) ifr.src = ifr.src + (ifr.src.indexOf('?') > -1 ? '&' : '?') + 'enablejsapi=1&origin=' + encodeURIComponent(location.origin);
      var listen = function () { try { ifr.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: rec.n, channel: 'widget' }), '*'); } catch (e) {} };
      ifr.addEventListener('load', listen); setTimeout(listen, 1500);
    }
    function scan(root) { Array.prototype.forEach.call((root || D).querySelectorAll ? (root || D).querySelectorAll('iframe') : [], attach); }
    if ('MutationObserver' in W) new MutationObserver(function (muts) {
      muts.forEach(function (m) { Array.prototype.forEach.call(m.addedNodes, function (nd) { if (nd.nodeType !== 1) return; if (nd.tagName === 'IFRAME') attach(nd); else scan(nd); }); });
    }).observe(D.documentElement, { childList: true, subtree: true });
    if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', function () { scan(); }); else scan();
    W.addEventListener('message', function (e) {
      if (!/youtube(-nocookie)?\.com$/.test(e.origin || '')) return;
      var d = e.data; if (typeof d === 'string') { try { d = JSON.parse(d); } catch (x) { return; } }
      if (!d || !d.event) return;
      var rec = null; for (var i = 0; i < players.length; i++) if (players[i].el.contentWindow === e.source) { rec = players[i]; break; }
      if (!rec) return;
      var st = d.event === 'onStateChange' ? d.info : (d.event === 'infoDelivery' && d.info && d.info.playerState != null ? d.info.playerState : null);
      if (st === 1 && !rec.played) { rec.played = 1; track('video_play', rec.key, 0, { muted: 0, auto: 0, title: rec.title }); }
      if (d.event === 'infoDelivery' && d.info && d.info.duration > 0 && d.info.currentTime > 0) {
        var pct = d.info.currentTime / d.info.duration * 100;
        [25, 50, 75, 95].forEach(function (m) { if (pct >= m && rec.best < m) { rec.best = m; track('video_progress', rec.key, m); } });
      }
      if (st === 0 && !rec.done) { rec.done = 1; if (rec.best < 95) { rec.best = 95; track('video_progress', rec.key, 95); } track('video_complete', rec.key, 100); }
    });
  })();

  // ---- tooltips + upsell ticks (all PDP heroes share .ua-info[data-ig-tip] / .ua-check[data-ig-add]) ----
  (function () {
    var last = {};
    function tip(el) {
      var k = el.getAttribute('data-ig-tip') || el.getAttribute('data-ygh-tip') || labelOf(el);
      var n = Date.now(); if (last[k] && n - last[k] < 5000) return; last[k] = n; seenTip[k] = n;
      track('tooltip_open', k, 0, { section: sectionOf(el) });
    }
    D.addEventListener('mouseover', function (e) {
      if (mobile) return;
      var el = e.target && e.target.closest ? e.target.closest('[data-ig-tip], [data-ygh-tip], .ua-info') : null; if (el) tip(el);
    });
    D.addEventListener('click', function (e) {
      var el = e.target && e.target.closest ? e.target.closest('[data-ig-tip], [data-ygh-tip], .ua-info') : null; if (el) tip(el);
    }, true);
    D.addEventListener('change', function (e) {
      var el = e.target; if (!el || !el.matches || !el.matches('input[data-ig-add], input[data-npwmp-accessory], input[data-ygh-add]')) return;
      var k = el.getAttribute('data-ig-add') || el.getAttribute('data-acc-product-id') || el.getAttribute('data-ygh-add') || 'acc';
      var after = seenTip[k] ? 1 : 0, anyTip = 0; for (var t in seenTip) if (seenTip.hasOwnProperty(t)) anyTip = 1;
      track(el.checked ? 'upsell_add' : 'upsell_remove', k, 0, { after_tip: after, any_tip: anyTip, tip_s: after ? Math.round((Date.now() - seenTip[k]) / 1000) : 0 });
    }, true);
  })();

  // ---- gallery: watch the active thumb ----
  function gallery() {
    var wraps = D.querySelectorAll('[data-npwmp-thumbs]');
    Array.prototype.forEach.call(wraps, function (wrap) {
      var thumbs = wrap.querySelectorAll('[data-npwmp-thumb]'), total = thumbs.length; if (total < 2 || !('MutationObserver' in W)) return;
      var cur = 0, maxIdx = 0, sec = sectionOf(wrap), fired = {}, since = Date.now();
      // time spent on each photo: sent when the photo changes and when the page is left (visible time only, 1 s to 10 min)
      function dwell() {
        var s = Math.round((Date.now() - since) / 1000); since = Date.now();
        if (D.visibilityState === 'hidden') return;
        if (s >= 1) track('gallery_dwell', cur + 1, Math.min(s, 600), { section: sec });
      }
      D.addEventListener('visibilitychange', function () { if (D.visibilityState === 'hidden') dwell(); else since = Date.now(); });
      W.addEventListener('pagehide', dwell);
      new MutationObserver(function () {
        var i = -1; for (var n = 0; n < total; n++) if (thumbs[n].classList.contains('active')) { i = n; break; }
        if (i < 0 || i === cur) return;
        dwell();
        cur = i; var kind = thumbs[i].querySelector('.thumb-play') ? 'video' : 'image';
        track('gallery_slide', i + 1, total, { kind: kind, section: sec });
        if (i > maxIdx) maxIdx = i;
        if (kind === 'video' && !fired.v) { fired.v = 1; track('gallery_video_view', i + 1, total); }
        if (i + 1 === 6 && !fired.m) { fired.m = 1; track('gallery_mid', 6, total); }
        if (i + 1 === total && !fired.c) { fired.c = 1; track('gallery_complete', total, total); }
      }).observe(wrap, { attributes: true, subtree: true, attributeFilter: ['class'] });
    });
    // full-screen photo viewer (tap on the main photo): opened on which photo, and every photo looked at inside it
    Array.prototype.forEach.call(D.querySelectorAll('[data-npwmp-lightbox]'), function (lb) {
      var count = lb.querySelector('[data-npwmp-lightbox-count]'), sec = sectionOf(lb), last = '';
      function at() { var m = /(\d+)\s*\/\s*(\d+)/.exec(count ? count.textContent : ''); return m ? [parseInt(m[1], 10), parseInt(m[2], 10)] : [0, 0]; }
      new MutationObserver(function () {
        if (lb.hidden) { last = ''; return; }
        var p = at(); if (!p[0]) return;
        var k = p[0] + '/' + p[1]; if (k === last) return;
        track(last ? 'gallery_zoom_slide' : 'gallery_zoom', p[0], p[1], { section: sec }); last = k;
      }).observe(lb, { attributes: true, attributeFilter: ['hidden'], childList: true, subtree: true, characterData: true });
    });
  }

  // ---- cart drawer, drawers, forms ----
  D.addEventListener('toggle', function (e) {
    var el = e.target; if (!el || !el.matches) return;
    if (el.matches('.cart-drawer-container') && el.open) { track('cart_open', page.prod); setTimeout(function () { tagCart(false); }, 1500); }
    else if (el.tagName === 'DETAILS' && el.open) { var s = el.querySelector('summary'); if (s) track('drawer_open', sectionOf(el) + '|' + labelOf(s)); }
  }, true);
  function drawers() {
    if (!('MutationObserver' in W)) return;
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        var el = m.target; if (!el.classList || !el.classList.contains('is-open')) return;
        if (el.__yghOpen) return; el.__yghOpen = 1; setTimeout(function () { el.__yghOpen = 0; }, 1500);
        var id = el.id || el.getAttribute('data-drawer') || el.getAttribute('data-npwmp-drawer') || el.className.split(' ')[0];
        track('drawer_open', sectionOf(el) + '|' + String(id).slice(0, 40));
      });
    }).observe(D.body, { attributes: true, subtree: true, attributeFilter: ['class'] });
  }
  D.addEventListener('hs-form-event:on-submission:success', function (e) {
    var id = (e.detail && e.detail.formId) || '';
    var t = e.target && e.target.closest ? e.target : D.body, sec = sectionOf(t);
    var cb = t.closest('[data-callback-embed], [data-cbm-embed], [data-callback-modal], .ygh-cbm, [data-callback-card], .callback-card');
    var kind = cb ? 'callback' : ((t.closest('.nn-surv2__embed') || /survey/i.test(sec)) ? 'survey' : 'form');
    if (kind === 'form' && intent) kind = intent;   // pop-up forms outside their section (sale / video pages): the button pressed last says what it was
    lastForm = id + '|' + Date.now();
    track('form_submit', id, 0, { kind: kind, section: sec });
    if (kind === 'callback') track('callback_submit', id, 0, { section: sec });
    if (kind === 'survey') track('survey_submit', id, 0, { section: sec });
  });
  // older HubSpot embeds (hbspt.forms.create: the Contact us page) report through window messages instead of the event above
  W.addEventListener('message', function (e) {
    var d = e.data; if (!d || d.type !== 'hsFormCallback' || d.eventName !== 'onFormSubmitted') return;
    var id = String(d.id || '');
    if (lastForm && lastForm.split('|')[0] === id && Date.now() - parseInt(lastForm.split('|')[1], 10) < 8000) return;
    lastForm = id + '|' + Date.now();
    track('form_submit', id, 0, { kind: 'contact', section: '' });
  });
  D.addEventListener('ygh:lead', function (e) {
    var d = e.detail || {};
    if (d.qualified !== null && d.qualified !== undefined) track('lead_qualified', d.formId, d.qualified ? 1 : 0, { answer: d.answer || '' });
    if (d.email) { store('l', 'ygh_wistia_email', d.email); wistiaEmail(); }
  });
  // ---- Wistia viewer identity ----
  function wistiaEmail() {
    var em = store('l', 'ygh_wistia_email'); if (!em) return;
    try { W.wistiaOptions = W.wistiaOptions || {}; W.wistiaOptions._all = W.wistiaOptions._all || {}; W.wistiaOptions._all.email = em; } catch (e) {}
    try { W._wq = W._wq || []; W._wq.push({ id: '_all', onReady: function (v) { try { if (typeof v.email === 'function') v.email(em); } catch (e) {} } }); } catch (e) {}
    Array.prototype.forEach.call(D.querySelectorAll('wistia-player'), function (el) {
      try { if (typeof el.email === 'function') el.email(em); else el.email = em; } catch (e) {}
    });
  }

  // ---- swipe rows: how far people get through a horizontal card row ----
  function carousels() {
    Array.prototype.forEach.call(D.querySelectorAll('[data-ygh-carousel], .f2y-strip, .nvst__points--swipe .nvst__list'), function (row) {
      var best = 0, t = null;
      row.addEventListener('scroll', function () {
        clearTimeout(t); t = setTimeout(function () {
          var kids = row.children, n = kids.length; if (n < 2 || row.scrollWidth <= row.clientWidth + 8) return;
          var mid = row.scrollLeft + row.clientWidth / 2, at = 0, bd = 1e9;
          for (var i = 0; i < n; i++) { var x = Math.abs(kids[i].offsetLeft - row.offsetLeft + kids[i].offsetWidth / 2 - mid); if (x < bd) { bd = x; at = i; } }
          if (row.scrollLeft + row.clientWidth >= row.scrollWidth - 8) at = n - 1;
          if (at > best) { best = at; track('carousel_step', sectionOf(row), at + 1, { total: n }); }
        }, 160);
      }, { passive: true });
    });
  }

  // any other sideways-scrolling block inside a section (comparison tables, card strips): how far it was swiped, 50% and to the end
  (function () {
    var hit = {}, t = null;
    D.addEventListener('scroll', function (e) {
      var el = e.target; if (!el || el === D || el.nodeType !== 1 || el.scrollWidth <= el.clientWidth + 24) return;
      if (el.matches('[data-ygh-carousel], .f2y-strip, .nvst__list') || el.closest('[data-npwmp-thumbs], #mini-cart, header-drawer')) return;
      clearTimeout(t); t = setTimeout(function () {
        var sec = sectionOf(el); if (!sec) return;
        var pct = (el.scrollLeft + el.clientWidth) / el.scrollWidth, start = el.clientWidth / el.scrollWidth;
        [[50, start + (1 - start) * 0.5], [100, 0.97]].forEach(function (m) { var k = sec + '|' + m[0]; if (pct >= m[1] && el.scrollLeft > 8 && !hit[k]) { hit[k] = 1; track('swipe_row', sec, m[0]); } });
      }, 180);
    }, true);
  })();

  function boot() { sectionViews(); gallery(); drawers(); carousels(); wistiaEmail(); }
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot); else boot();
})();
